import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from './Spinner';
import { apiService } from '../services/apiService';
import { RotateCwIcon } from './icons/RotateCwIcon';
import { RotateCcwIcon } from './icons/RotateCcwIcon';
import { ScanIcon } from './icons/ScanIcon';
import { ResetIcon } from './icons/ResetIcon';
import { logger } from '../services/logger';
import { COLOR_EFFECTS, ColorEffect } from '../types';
import { getScannerSettings } from '../services/settingsService';

type Point = { x: number; y: number };

interface ScanEditorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (newImageSrc: string) => void;
    imageSrc: string;
}

export const ScanEditorModal: React.FC<ScanEditorModalProps> = ({ isOpen, onClose, onConfirm, imageSrc }) => {
    const [currentImage, setCurrentImage] = useState(imageSrc);
    const [originalImage] = useState(imageSrc);
    const [rotation, setRotation] = useState(0);
    const [corners, setCorners] = useState<Point[]>([]);
    const [isManualCropMode, setIsManualCropMode] = useState(false);
    const [colorEffect, setColorEffect] = useState<ColorEffect>('bw_adaptive_mean');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [draggingCornerIndex, setDraggingCornerIndex] = useState<number | null>(null);

    const imageRef = useRef<HTMLImageElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const handleReset = useCallback(() => {
        setCurrentImage(originalImage);
        setRotation(0);
        setCorners([]);
        setIsManualCropMode(false);
        setError(null);
    }, [originalImage]);
    
    useEffect(() => {
        if (isOpen) {
            handleReset();
        }
    }, [isOpen, handleReset]);

    // Načtení výchozího efektu pouze při prvním mountu komponenty.
    useEffect(() => {
        getScannerSettings().then(settings => {
            setColorEffect(settings.defaultPaperSnapEffect);
        }).catch(err => {
            logger.error("Failed to load default PaperSnap effect on mount", err);
            setColorEffect('bw_adaptive_mean');
        });
    }, []);

    const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isManualCropMode || corners.length >= 4 || !imageRef.current) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const img = imageRef.current;

        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const naturalX = Math.round((x / img.width) * img.naturalWidth);
        const naturalY = Math.round((y / img.height) * img.naturalHeight);

        setCorners(prev => [...prev, { x: naturalX, y: naturalY }]);
    };
    
    const handleCornerAction = (e: React.MouseEvent | React.TouchEvent, index: number) => {
        e.stopPropagation();
        setDraggingCornerIndex(index);
    };

    const handleMove = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        if (draggingCornerIndex === null || !imageRef.current) return;

        const container = e.currentTarget;
        const rect = container.getBoundingClientRect();
        const img = imageRef.current;

        let clientX, clientY;
        if ('touches' in e) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }

        const x = clientX - rect.left;
        const y = clientY - rect.top;
        
        const clampedX = Math.max(0, Math.min(x, img.width));
        const clampedY = Math.max(0, Math.min(y, img.height));

        const naturalX = Math.round((clampedX / img.width) * img.naturalWidth);
        const naturalY = Math.round((clampedY / img.height) * img.naturalHeight);

        setCorners(prev => {
            const newCorners = [...prev];
            if (newCorners[draggingCornerIndex]) {
                 newCorners[draggingCornerIndex] = { x: naturalX, y: naturalY };
            }
            return newCorners;
        });
    };
    
    const handleMoveEnd = () => {
        setDraggingCornerIndex(null);
    };

    const handleProcessWithPaperSnap = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const processedBlob = await apiService.scanDocument(originalImage, colorEffect, corners.length === 4 ? corners : null);
            const newImageUrl = URL.createObjectURL(processedBlob);
            setCurrentImage(newImageUrl);
            setCorners([]);
            setIsManualCropMode(false);
        } catch (e: any) {
            setError('Skenování selhalo: ' + e.message);
        } finally {
            setIsLoading(false);
        }
    };
    
    const applyRotation = (angle: number) => {
        const newRotation = (rotation + angle + 360) % 360;
        setRotation(newRotation);
    };

    const handleConfirm = () => {
        if (rotation !== 0) {
            const img = new Image();
            img.onload = () => {
                const canvas = canvasRef.current;
                if (!canvas) return;
                
                const ctx = canvas.getContext('2d');
                if (!ctx) return;

                const rad = rotation * Math.PI / 180;
                const newWidth = Math.abs(img.width * Math.cos(rad)) + Math.abs(img.height * Math.sin(rad));
                const newHeight = Math.abs(img.width * Math.sin(rad)) + Math.abs(img.height * Math.cos(rad));

                canvas.width = newWidth;
                canvas.height = newHeight;
                
                ctx.translate(newWidth / 2, newHeight / 2);
                ctx.rotate(rad);
                ctx.drawImage(img, -img.width / 2, -img.height / 2);
                
                onConfirm(canvas.toDataURL('image/jpeg', 1.0));
            };
            img.src = currentImage;
        } else {
            onConfirm(currentImage);
        }
    };

    const isSideways = rotation === 90 || rotation === 270;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-[60] flex flex-col items-center justify-center text-white" onClick={onClose}>
            <canvas ref={canvasRef} className="hidden" />
            <div className="relative w-full h-full flex flex-col p-4" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <header className="flex-shrink-0 flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Upravit sken</h2>
                    <div>
                        <button onClick={onClose} className="px-4 py-2 hover:bg-gray-700 rounded-md">Zavřít</button>
                    </div>
                </header>

                {/* Main Content */}
                <main className="flex-grow flex items-center justify-center min-h-0">
                    {isLoading ? <Spinner text="Zpracovávám obrázek..." /> : error ? (
                        <div className="text-center text-red-400">{error}</div>
                    ) : (
                        <div 
                            className="relative" 
                            onClick={handleImageClick}
                            onMouseMove={handleMove}
                            onMouseUp={handleMoveEnd}
                            onMouseLeave={handleMoveEnd}
                            onTouchMove={handleMove}
                            onTouchEnd={handleMoveEnd}
                            style={{ cursor: isManualCropMode ? 'crosshair' : 'default' }}
                        >
                            <img 
                                ref={imageRef} 
                                src={currentImage} 
                                alt="Náhled skenu" 
                                className={`object-contain transition-transform duration-200 ${isSideways ? 'max-h-[70vh] max-w-full sm:max-w-[70vh]' : 'max-w-full max-h-[70vh]'}`}
                                style={{ transform: `rotate(${rotation}deg)` }} 
                            />
                            {isManualCropMode && corners.map((corner, index) => {
                                const rect = imageRef.current?.getBoundingClientRect();
                                if (!rect || !imageRef.current) return null;
                                const x = (corner.x / imageRef.current.naturalWidth) * rect.width;
                                const y = (corner.y / imageRef.current.naturalHeight) * rect.height;
                                return (
                                    <div 
                                        key={index}
                                        onMouseDown={(e) => handleCornerAction(e, index)}
                                        onTouchStart={(e) => handleCornerAction(e, index)}
                                        className="absolute w-5 h-5 bg-red-500 border-2 border-white rounded-full -translate-x-1/2 -translate-y-1/2 cursor-move z-10" 
                                        style={{ left: x, top: y }}
                                    />
                                );
                            })}
                        </div>
                    )}
                </main>

                {/* Footer Toolbar */}
                <footer className="flex-shrink-0 mt-4 space-y-4">
                    <div className="flex justify-center items-center gap-4 flex-wrap bg-gray-900 bg-opacity-50 p-3 rounded-lg">
                        <button onClick={() => applyRotation(-90)} title="Otočit doleva" className="p-2 hover:bg-gray-700 rounded-full"><RotateCcwIcon className="w-6 h-6" /></button>
                        <button onClick={() => applyRotation(90)} title="Otočit doprava" className="p-2 hover:bg-gray-700 rounded-full"><RotateCwIcon className="w-6 h-6" /></button>
                        
                        <div className="h-8 border-l border-gray-600 mx-2"></div>

                        <select value={colorEffect} onChange={e => setColorEffect(e.target.value as ColorEffect)} className="bg-gray-800 border border-gray-600 rounded-md p-2 text-sm">
                            {COLOR_EFFECTS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                        
                        <button onClick={() => setIsManualCropMode(!isManualCropMode)} title="Manuální ořez" className={`p-2 rounded-full ${isManualCropMode ? 'bg-blue-600' : 'hover:bg-gray-700'}`}>
                            <ScanIcon className="w-6 h-6" />
                        </button>
                        {corners.length > 0 && <span className="text-sm">{corners.length}/4 rohů</span>}
                         
                        <div className="h-8 border-l border-gray-600 mx-2"></div>
                        
                        <button onClick={handleProcessWithPaperSnap} disabled={isLoading} className="px-4 py-2 bg-blue-600 rounded-md text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-500">
                           Vylepšit sken
                        </button>
                        <button onClick={handleReset} title="Resetovat úpravy" className="p-2 hover:bg-gray-700 rounded-full"><ResetIcon className="w-6 h-6" /></button>
                    </div>

                     <div className="flex justify-center items-center gap-4">
                        <button onClick={onClose} className="px-6 py-2 bg-gray-700 rounded-md text-sm font-semibold hover:bg-gray-600">Zrušit</button>
                        <button onClick={handleConfirm} className="px-6 py-2 bg-green-600 rounded-md text-sm font-semibold hover:bg-green-700">Potvrdit změny</button>
                    </div>
                </footer>
            </div>
        </div>
    );
};